// AKFA integratsiyasi — constructor-admin `/v1/akfa/**` endpointlari.
// Barcha funksiyalar { success, data } yoki { success: false, status, message } qaytaradi.
// Hujjat: AKFA_FRONTEND_API.md

const BASE_URL = `${process.env.REACT_APP_HOST_BASE_URL}/v1/akfa`;

// `page` 1 dan boshlanadi — FilterRequest.toPageRequest() konvensiyasi (AKFA_FRONTEND_API.md, 7-bo'lim)
export const AKFA_PAGE_BASE = 1;

const buildHeaders = (token) => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
});

const buildQuery = (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") query.append(key, value);
    });
    const str = query.toString();
    return str ? `?${str}` : "";
};

// 409 biznes-xatolar — oddiy matn; boshqalar — Spring xato obyekti ({message}).
async function parseError(response) {
    try {
        const text = await response.text();
        if (!text) return null;
        try {
            const json = JSON.parse(text);
            if (typeof json === "string") return json;
            return json?.message || json?.errorMessage || json?.error || text;
        } catch {
            return text;
        }
    } catch {
        return null;
    }
}

async function parseBody(response) {
    const text = await response.text();
    if (!text) return null;
    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
}

async function request(token, path, { method = "GET", body } = {}) {
    try {
        const response = await fetch(`${BASE_URL}${path}`, {
            method,
            headers: buildHeaders(token),
            body: body !== undefined ? JSON.stringify(body) : undefined,
        });
        if (response.ok) {
            return { success: true, status: response.status, data: await parseBody(response) };
        }
        return { success: false, status: response.status, message: await parseError(response) };
    } catch (error) {
        return { success: false, message: error.message };
    }
}

// ---------- Summary ----------

export const getAkfaSummary = (token) => request(token, "/summary");

// ---------- Inbound messages (Jurnal) ----------

export const getAkfaMessages = (token, { status, type, page, size } = {}) =>
    request(token, `/messages${buildQuery({ status, type, page, size })}`);

export const getAkfaMessageById = (token, id) => request(token, `/messages/${id}`);

export const retryAkfaMessage = (token, id) => request(token, `/messages/${id}/retry`, { method: "POST" });

export const recalculateAkfaRates = (token) => request(token, "/rates/recalculate", { method: "POST" });

// ---------- AKFA products ----------

export const getAkfaProducts = (
    token,
    { search, externalStatus, hasRate, mappingStatus, priceStatus, isNew, source, group, page, size } = {}
) =>
    request(
        token,
        `/products${buildQuery({ search, externalStatus, hasRate, mappingStatus, priceStatus, isNew, source, group, page, size })}`
    );

export const getAkfaProductById = (token, id) => request(token, `/products/${id}`);

export const mapAkfaProduct = (token, id, body) =>
    request(token, `/products/${id}/map`, { method: "POST", body });

export const ignoreAkfaProducts = (token, { ids, reason }) =>
    request(token, "/products/ignore", { method: "POST", body: { ids, reason } });

export const reopenAkfaProduct = (token, id) => request(token, `/products/${id}/reopen`, { method: "POST" });

export const keepAkfaProduct = (token, id) => request(token, `/products/${id}/keep`, { method: "POST" });

export const markAkfaProductSeen = (token, id) => request(token, `/products/${id}/mark-seen`, { method: "POST" });

export const getAkfaProductDraft = (token, id) => request(token, `/products/${id}/product-draft`);

export const createProductFromAkfa = (token, id, body) =>
    request(token, `/products/${id}/create-product`, { method: "POST", body });

export async function exportAkfaUnmappedProducts(token) {
    try {
        const response = await fetch(`${BASE_URL}/products/export`, {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
            return { success: false, status: response.status, message: await parseError(response) };
        }
        const disposition = response.headers.get("Content-Disposition") || "";
        const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
        return {
            success: true,
            data: await response.blob(),
            fileName: match ? decodeURIComponent(match[1]) : "akfa-unmapped-products.xlsx",
        };
    } catch (error) {
        return { success: false, message: error.message };
    }
}

// ---------- WinPro products (Mapping tab, "WinPro mahsulotlari" sub-tab) ----------
// linkStatus: all | mapped | unmapped

export const getAkfaWinproProducts = (token, { search, linkStatus, productSeriesId, articleGroupType, page, size } = {}) =>
    request(
        token,
        `/winpro-products${buildQuery({ search, linkStatus, productSeriesId, articleGroupType, page, size })}`
    );

export const getAkfaWinproTextureOptions = (token, productId) =>
    request(token, `/winpro-products/${productId}/texture-options`);

// ---------- WinPro positions ----------

export const searchAkfaPositions = (token, { q, productSeriesId, articleGroupType } = {}) =>
    request(token, `/positions/search${buildQuery({ q, productSeriesId, articleGroupType })}`);

// ---------- Mappings ----------

export const getAkfaMappings = (token, { status, priceStatus, search, minDelta, page, size } = {}) =>
    request(token, `/mappings${buildQuery({ status, priceStatus, search, minDelta, page, size })}`);

export const updateAkfaMapping = (token, id, { conversionMode, factor }) =>
    request(token, `/mappings/${id}`, { method: "PUT", body: { conversionMode, factor } });

export const deleteAkfaMapping = (token, id) => request(token, `/mappings/${id}`, { method: "DELETE" });

// ---------- Mapping modali (tekstura ↔ Akfa tovari, N:1) ----------

// Teksturalar: GET /winpro-products/{productId}/texture-options (getAkfaWinproTextureOptions, yuqorida)

// Akfa tovarlari + har biriga ulangan teksturalar (chip uchun). linkStatus: all | mapped | unmapped
// → { data: [{ id, externalId, name, groupId, groupName, price, currency, unitName, linkedTextureCount, linkedTextures }], total, page, size }
export const getAkfaMappingAkfaProducts = (token, { search, linkStatus, page, size } = {}) =>
    request(token, `/mappings/akfa-products${buildQuery({ search, linkStatus, page, size })}`);

// Faqat o'zgargan juftliklar: { changes: [{ productId, textureId, akfaProductId, expectedAkfaProductId }] }
// 409 da javob tanasi ham qaytariladi (to'qnashuvlar ro'yxati bo'lishi mumkin)
export async function saveAkfaMappingsBatch(token, changes) {
    try {
        const response = await fetch(`${BASE_URL}/mappings/batch`, {
            method: "PUT",
            headers: buildHeaders(token),
            body: JSON.stringify({ changes }),
        });
        const data = await parseBody(response);
        if (response.ok) return { success: true, status: response.status, data };
        const message = typeof data === "string" ? data : data?.message || data?.errorMessage || data?.error || null;
        return { success: false, status: response.status, data, message };
    } catch (error) {
        return { success: false, message: error.message };
    }
}

// ---------- Mapping Excel import (ommaviy bog'lash) ----------

export async function exportAkfaMappingsTemplate(token) {
    try {
        const response = await fetch(`${BASE_URL}/mappings/export-template`, {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
            return { success: false, status: response.status, message: await parseError(response) };
        }
        const disposition = response.headers.get("Content-Disposition") || "";
        const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
        return {
            success: true,
            data: await response.blob(),
            fileName: match ? decodeURIComponent(match[1]) : "akfa-mapping-template.xlsx",
        };
    } catch (error) {
        return { success: false, message: error.message };
    }
}

// Javob tanasi 200 (natija) va 409 (to'qnashuvlar, hech narsa saqlanmagan) holatlarida ham qaytariladi
export async function importAkfaMappingsExcel(token, file) {
    try {
        const formData = new FormData();
        formData.append("file", file);
        const response = await fetch(`${BASE_URL}/mappings/import/excel`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
        });
        const data = await parseBody(response);
        if (response.ok) return { success: true, status: response.status, data };
        const message = typeof data === "string" ? data : data?.message || data?.errorMessage || data?.error || null;
        return { success: false, status: response.status, data, message };
    } catch (error) {
        return { success: false, message: error.message };
    }
}

export const approveAkfaMappingPrice = (token, id, force = false) =>
    request(token, `/mappings/${id}/approve-price?force=${force}`, { method: "PUT" });

export const rejectAkfaMappingPrice = (token, id) =>
    request(token, `/mappings/${id}/reject-price`, { method: "PUT" });

export const bulkApproveAkfaMappingPrice = (token, { ids, force = false }) =>
    request(token, "/mappings/bulk-approve-price", { method: "PUT", body: { ids, force } });

export const bulkRejectAkfaMappingPrice = (token, { ids }) =>
    request(token, "/mappings/bulk-reject-price", { method: "PUT", body: { ids } });

// ---------- Unit mappings (AKFA birligi → WinPro c_unit) ----------
// unitName/unitShortName serverda to'ldiriladi — faqat akfaUnitId, akfaUnitName, unitId yuboriladi

export const getAkfaUnitMappings = (token) => request(token, "/unit-mappings");

export const createAkfaUnitMapping = (token, { akfaUnitId, akfaUnitName, unitId }) =>
    request(token, "/unit-mappings", { method: "POST", body: { akfaUnitId, akfaUnitName, unitId } });

export const updateAkfaUnitMapping = (token, id, { akfaUnitId, akfaUnitName, unitId }) =>
    request(token, `/unit-mappings/${id}`, { method: "PUT", body: { id, akfaUnitId, akfaUnitName, unitId } });

export const deleteAkfaUnitMapping = (token, id) => request(token, `/unit-mappings/${id}`, { method: "DELETE" });
